import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { API_CONFIG } from "../config/environment";
import { cacheConfig } from './performanceEnhancements';
import { supabase } from './supabase';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(res.status + ': ' + text);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const fullUrl = API_CONFIG.getApiUrl(url);

  // Get Supabase session for authentication
  const { data: { session } } = await supabase.auth.getSession();

  // Build headers with authentication token
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "Accept": "application/json",
  };

  // Add Bearer token if session exists
  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
    console.log('[API-REQUEST] Including Bearer token for:', method, url);
  } else {
    console.warn('[API-REQUEST] No session found, request may fail:', method, url);
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include", // Also include cookies for backwards compatibility
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = API_CONFIG.getApiUrl(queryKey[0] as string);

    // Get Supabase session for authentication
    const { data: { session } } = await supabase.auth.getSession();

    // Build headers with authentication token
    const headers: HeadersInit = {
      "Accept": "application/json",
    };

    // Add Bearer token if session exists
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    const res = await fetch(url, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Strategic caching based on data volatility
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      staleTime: 2 * 60 * 1000, // 2 minutes default - MAJOR PERFORMANCE BOOST
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      refetchOnWindowFocus: false, // Keep disabled for performance
      refetchOnMount: "always", // Fresh data on mount
      refetchInterval: false, // Disable auto-polling by default
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403 errors
        if (error?.message?.includes('401') || error?.message?.includes('403')) {
          return false;
        }
        return failureCount < 2; // Retry up to 2 times
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors (4xx)
        if (error?.message?.includes('4')) {
          return false;
        }
        return failureCount < 1; // Retry once for server errors
      },
      retryDelay: 2000,
    },
  },
});
